/* search-drawer.js — <search-drawer> custom element. Mobile full-page search that opens below
   the header (like menu/cart drawers) and shows live predictive results via the Section
   Rendering variant of the Predictive Search API. Framework-free, deferred. Desktop keeps the
   header search link navigating to /search. */
(function () {
  'use strict';

  var SECTION = 'predictive-search';
  var MIN_CHARS = 2;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class SearchDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('#SearchDrawer');
      this.input = this.querySelector('[data-predictive-input]');
      this.host = this.querySelector('[data-predictive-host]');
      this.def = this.querySelector('[data-search-default]');
      this.toggle = document.querySelector('[data-search-drawer-toggle]');
      this.reqId = 0;
      this.debounce = null;

      // Opens the search pane on every viewport (a full pane on mobile, a panel below the
      // header on desktop). The <a href="/search"> stays a working no-JS fallback.
      if (this.toggle) {
        this.toggle.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.classList.contains('is-open')) this.close();
          else this.open();
        });
      }

      this.querySelectorAll('[data-search-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // Click outside the pane (and outside the toggle) closes it.
      document.addEventListener('click', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (this.drawer.contains(e.target)) return;
        if (this.toggle && this.toggle.contains(e.target)) return;
        this.close();
      });

      if (this.input) {
        this.input.addEventListener('input', () => {
          clearTimeout(this.debounce);
          this.debounce = setTimeout(() => this.run(), 220);
        });
      }

      // Popular-search pills: fill the field and search inline.
      this.addEventListener('click', (e) => {
        var pill = e.target.closest('[data-search-term]');
        if (!pill) return;
        this.input.value = pill.getAttribute('data-search-term');
        this.input.focus();
        this.run();
      });
    }

    open() {
      // The menu marks this pane inert. Close it first so the field can take
      // focus. restoreFocus:false matches the cart: the burger must not take
      // focus while this sheet is still visibility:hidden.
      var menu = document.querySelector('menu-drawer.is-open');
      if (menu && typeof menu.close === 'function') menu.close({ restoreFocus: false });
      // Cancel a close that has not finished hiding the pane.
      this._closeToken = (this._closeToken || 0) + 1;
      var header = document.querySelector('.site-header');
      if (header) this.drawer.style.setProperty('--header-h', header.offsetHeight + 'px');
      this.drawer.hidden = false;
      if (this._openFrame) cancelAnimationFrame(this._openFrame);
      this._openFrame = requestAnimationFrame(() => {
        this._openFrame = 0;
        if (!this.drawer || this.drawer.hidden) return;
        this.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      // Focus after the open transition so mobile keyboards do not fight the animation.
      clearTimeout(this._focusTimer);
      this._focusTimer = setTimeout(() => {
        this._focusTimer = 0;
        if (!this.classList.contains('is-open')) return;
        if (this.input) this.input.focus();
      }, 260);
    }

    close() {
      if (this._openFrame) {
        cancelAnimationFrame(this._openFrame);
        this._openFrame = 0;
      }
      clearTimeout(this._focusTimer);
      this._focusTimer = 0;
      // Return focus only when it is still inside the pane (or lost).
      // Clicking another control, such as the cart toggle, must keep that focus.
      var searchFocus = document.activeElement;
      if (this.toggle && (!searchFocus || searchFocus === document.body || this.drawer.contains(searchFocus))) {
        this.toggle.focus({ preventScroll: true });
      }
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      this._closeToken = (this._closeToken || 0) + 1;
      var token = this._closeToken;
      var finish = () => {
        if (token !== this._closeToken) return;
        if (this.classList.contains('is-open')) return;
        this._closeToken = (this._closeToken || 0) + 1;
        this.drawer.hidden = true;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      var onEnd = (e) => {
        if (e.target !== this.drawer) return;
        this.drawer.removeEventListener('transitionend', onEnd);
        finish();
      };
      this.drawer.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, 450);
    }

    showDefault() {
      if (this.def) this.def.hidden = false;
      if (this.host) { this.host.hidden = true; this.host.innerHTML = ''; }
    }

    run() {
      var q = (this.input.value || '').trim();
      if (q.length < MIN_CHARS) { this.showDefault(); return; }

      var id = ++this.reqId;
      var url = root() + 'search/suggest?q=' + encodeURIComponent(q) +
        '&resources[type]=query,product,collection' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last' +
        '&section_id=' + SECTION;

      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          if (id !== this.reqId) return; // a newer keystroke already fired
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var results = doc.querySelector('[data-predictive-results]');
          if (!results || !this.host) return;
          this.host.innerHTML = results.outerHTML;
          this.host.hidden = false;
          if (this.def) this.def.hidden = true;
        })
        .catch(() => { if (id === this.reqId) this.showDefault(); });
    }
  }

  if (!customElements.get('search-drawer')) customElements.define('search-drawer', SearchDrawer);
})();
